// src/contexts/PanierContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

import { ProfilContext } from "./ProfilContext";
import { auditPrice } from "../utils/priceAuditor";

/**
 * ===============================================================
 * PanierContext.js
 * ===============================================================
 *
 * TEMPLATE MAINTENEUR — GUIDE MANUEL
 * ---------------------------------------------------------------
 * RÔLE DE CE CONTEXT
 * ---------------------------------------------------------------
 * Ce provider centralise :
 * - le contenu du panier
 * - sa persistance localStorage
 * - son sous-total
 * - les actions add / remove / update qty
 *
 * BUG MÉTIER TRAITÉ ICI
 * ---------------------------------------------------------------
 * Cas réel Marché Deluxe :
 *
 * - visiteur ajoute un produit à 9 CHF
 * - il se connecte
 * - le produit doit passer automatiquement à 7 CHF
 *
 * Avant :
 * - le panier stockait un prix "figé"
 * - aucune réévaluation explicite n’était faite
 * - le sous-total interne pouvait rester sur une vieille valeur
 *
 * OBJECTIF DE CE FICHIER
 * ---------------------------------------------------------------
 * 1) enrichir les lignes panier avec les sources pricing utiles
 * 2) recalculer le panier quand le profil change
 * 3) garder une persistance locale robuste
 * 4) rester drop-in et compatible avec l’existant
 *
 * IMPORTANT
 * ---------------------------------------------------------------
 * Ce fichier suppose idéalement que PanierProvider soit utilisé
 * sous ProfilProvider.
 *
 * Si ProfilContext n’est pas disponible :
 * - le panier reste fonctionnel
 * - il se comporte en mode visiteur
 *
 * NOTE CHANTIER RECOMMANDATIONS PANIER
 * ---------------------------------------------------------------
 * Le chantier "Nous vous recommandons" ne change pas la responsabilité
 * centrale du context :
 *
 * - le context continue uniquement à gérer le panier
 * - les recommandations sont pilotées depuis PagePanier
 * - PagePanier réutilise simplement ajouterAuPanier()
 *
 * Cela évite d’alourdir le provider avec une logique merchandising.
 *
 * ===============================================================
 */

export const PanierContext = createContext(null);
export const usePanier = () => useContext(PanierContext);

const LS_KEY = "panier";

/**
 * ---------------------------------------------------------------
 * HELPER — CLÉ UNIQUE LIGNE PANIER
 * ---------------------------------------------------------------
 *
 * NOTE
 * ---------------------------------------------------------------
 * On garde la logique existante :
 * - une même référence produit
 * - + distinction par grams
 *
 * IMPORTANT
 * ---------------------------------------------------------------
 * Cela permet de différencier correctement :
 * - un même fromage en plusieurs poids
 * - un même produit simple
 * - des variantes qui passent par grams
 */
const makeCartKey = (id, grams) => `${String(id)}:${Number(grams || 0) || 0}`;

/**
 * ---------------------------------------------------------------
 * HELPER — NORMALISATION CENTIMES
 * ---------------------------------------------------------------
 *
 * Objectif :
 * - accepter formats variés
 * - toujours renvoyer des centimes
 * - rester tolérant au legacy
 *
 * Cas gérés :
 * - 7.5     -> 750
 * - "7.50"  -> 750
 * - 750     -> 750
 * - null    -> null
 */
const toCents = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  if (Number.isInteger(n) && n >= 100) {
    return Math.round(n);
  }

  return Math.round(n * 100);
};

/**
 * ---------------------------------------------------------------
 * HELPER — SNAPSHOT PRICING D’UN ITEM PANIER
 * ---------------------------------------------------------------
 *
 * RÔLE
 * ---------------------------------------------------------------
 * Reprend un item et lui applique la vérité pricing actuelle
 * selon le profil connecté / visiteur.
 *
 * IMPORTANT
 * ---------------------------------------------------------------
 * On conserve :
 * - price                = prix affiché / appliqué maintenant
 * - displayPrice         = idem
 * - appliedPrice         = idem
 * - basePrice            = prix public / visiteur
 * - memberPrice          = prix membre si présent
 * - old_price            = base si avantage membre
 * - pricingMode          = mode canonique du moteur
 *
 * IMPORTANT RECO PANIER
 * ---------------------------------------------------------------
 * Les produits recommandés ajoutés au panier passent aussi ici.
 * Donc même si leur payload est plus léger, on reconstruit ensuite
 * une ligne panier cohérente avec le moteur pricing du projet.
 */
const applyPricingSnapshot = (item, profilPricing, userProfil) => {
  const audit = auditPrice(item, { profilPricing, userProfil });

  const safeDisplayPrice =
    toCents(audit?.displayPrice) ??
    toCents(item?.displayPrice) ??
    toCents(item?.price) ??
    0;

  const safeBasePrice =
    toCents(audit?.basePrice) ??
    toCents(item?.basePrice) ??
    toCents(item?.publicPrice) ??
    toCents(item?.old_price) ??
    safeDisplayPrice;

  const safeMemberPrice =
    toCents(item?.memberPrice) ??
    toCents(item?.pricePro) ??
    toCents(item?.prix_connecte) ??
    toCents(item?.prix_membre) ??
    toCents(audit?.targetPrice) ??
    null;

  return {
    ...item,

    /**
     * -----------------------------------------------------------
     * PRIX APPLIQUÉ MAINTENANT
     * -----------------------------------------------------------
     * Ce sont les champs effectivement utilisés par le panier
     * et ses vues d’affichage.
     */
    price: safeDisplayPrice,
    displayPrice: safeDisplayPrice,
    appliedPrice: safeDisplayPrice,

    /**
     * -----------------------------------------------------------
     * SOURCES PRIX
     * -----------------------------------------------------------
     * On garde les références utiles pour :
     * - le re-pricing futur
     * - la cohérence des composants front
     * - les compat legacy
     */
    basePrice: safeBasePrice,
    publicPrice: safeBasePrice,
    memberPrice: safeMemberPrice,
    old_price:
      safeMemberPrice !== null && safeMemberPrice < safeBasePrice
        ? safeBasePrice
        : toCents(item?.old_price),

    /**
     * -----------------------------------------------------------
     * COMPATIBILITÉ LEGACY MEMBRE
     * -----------------------------------------------------------
     * Plusieurs zones historiques du projet peuvent encore relire
     * l’un de ces champs.
     */
    pricePro:
      toCents(item?.pricePro) ??
      toCents(item?.memberPrice) ??
      toCents(audit?.targetPrice),

    prix_connecte:
      toCents(item?.prix_connecte) ??
      toCents(item?.memberPrice) ??
      toCents(audit?.targetPrice),

    prix_membre:
      toCents(item?.prix_membre) ??
      toCents(item?.memberPrice) ??
      toCents(audit?.targetPrice),

    /**
     * -----------------------------------------------------------
     * MÉTA PRICING
     * -----------------------------------------------------------
     * pricingMode permet de savoir rapidement si la ligne est
     * affichée en mode public, membre, promo, etc.
     */
    pricingMode: audit?.pricingMode || item?.pricingMode || "PUBLIC",
    audit,
  };
};

/**
 * ---------------------------------------------------------------
 * HELPER — NORMALISATION LIGNE PANIER
 * ---------------------------------------------------------------
 *
 * OBJECTIF
 * ---------------------------------------------------------------
 * Uniformiser les données :
 * - grams
 * - qty
 * - cartKey
 * - champs pricing critiques
 * - champs image utiles au rendu panier
 *
 * IMPORTANT
 * ---------------------------------------------------------------
 * Ce helper doit préserver une image exploitable pour tous les items,
 * y compris ceux ajoutés depuis les recommandations panier.
 */
const normalizeCartItem = (item) => {
  if (!item) return item;

  const grams = Number(item.grams || 0) || 0;
  const cartKey = item.cartKey || makeCartKey(item.id, grams);
  const qty = Number(item.qty || item.quantite || item.quantity || 1) || 1;

  /**
   * -------------------------------------------------------------
   * NORMALISATION IMAGE
   * -------------------------------------------------------------
   * On tente de récupérer une source image exploitable depuis les
   * différentes conventions déjà présentes dans le projet.
   */
  const rawImage =
    item?.imageUrl ||
    item?.image_url ||
    item?.image ||
    item?.photo ||
    item?.thumbnail ||
    "";

  return {
    ...item,
    grams,
    cartKey,
    qty,

    /**
     * -----------------------------------------------------------
     * CHAMPS IMAGE CONSERVÉS
     * -----------------------------------------------------------
     * On réinjecte explicitement toutes les variantes utiles afin que
     * le rendu panier retrouve toujours une image dès le premier ajout.
     */
    image: rawImage || item?.image || "",
    imageUrl: rawImage || item?.imageUrl || "",
    image_url: rawImage || item?.image_url || "",
    photo: rawImage || item?.photo || "",
    thumbnail: rawImage || item?.thumbnail || "",

    /**
     * -----------------------------------------------------------
     * NORMALISATION PRIX
     * -----------------------------------------------------------
     * Tous ces champs sont remis dans un état cohérent en centimes.
     */
    price: toCents(item?.price) ?? 0,
    displayPrice: toCents(item?.displayPrice) ?? toCents(item?.price) ?? 0,
    appliedPrice:
      toCents(item?.appliedPrice) ??
      toCents(item?.displayPrice) ??
      toCents(item?.price) ??
      0,

    basePrice:
      toCents(item?.basePrice) ??
      toCents(item?.publicPrice) ??
      toCents(item?.old_price) ??
      toCents(item?.price) ??
      0,

    publicPrice:
      toCents(item?.publicPrice) ??
      toCents(item?.basePrice) ??
      toCents(item?.old_price) ??
      toCents(item?.price) ??
      0,

    memberPrice:
      toCents(item?.memberPrice) ??
      toCents(item?.pricePro) ??
      toCents(item?.prix_connecte) ??
      toCents(item?.prix_membre) ??
      null,

    old_price: toCents(item?.old_price),

    pricePro:
      toCents(item?.pricePro) ??
      toCents(item?.memberPrice) ??
      toCents(item?.prix_connecte) ??
      toCents(item?.prix_membre),

    prix_connecte:
      toCents(item?.prix_connecte) ??
      toCents(item?.memberPrice) ??
      toCents(item?.pricePro),

    prix_membre:
      toCents(item?.prix_membre) ??
      toCents(item?.memberPrice) ??
      toCents(item?.pricePro),
  };
};

export const PanierProvider = ({ children }) => {
  const profilCtx = useContext(ProfilContext);

  /**
   * -------------------------------------------------------------
   * PROFIL COURANT
   * -------------------------------------------------------------
   * Si ProfilContext absent :
   * - fallback visiteur
   */
  const userProfil = profilCtx?.userProfil ?? null;
  const profilPricing = profilCtx?.profilPricing ?? null;
  const authStatus = profilCtx?.authStatus ?? "visitor";

  const [panier, setPanier] = useState([]);
  const [sousTotal, setSousTotal] = useState(0);

  /**
   * -------------------------------------------------------------
   * HYDRATATION
   * -------------------------------------------------------------
   * Évite d’écraser le LS par [] au boot.
   */
  const [isHydrated, setIsHydrated] = useState(false);

  /**
   * =============================================================
   * 1) HYDRATATION LOCALSTORAGE
   * =============================================================
   *
   * RÔLE
   * -------------------------------------------------------------
   * On relit le panier persistant et on renormalise chaque ligne
   * immédiatement pour éviter les incohérences legacy.
   */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];

      if (Array.isArray(parsed)) {
        setPanier(parsed.map(normalizeCartItem));
      } else {
        setPanier([]);
      }
    } catch {
      setPanier([]);
    } finally {
      setIsHydrated(true);
    }
  }, []);

  /**
   * =============================================================
   * 2) RE-PRICING AUTOMATIQUE SUR CHANGEMENT PROFIL / AUTH
   * =============================================================
   *
   * OBJECTIF
   * -------------------------------------------------------------
   * Si le visiteur se connecte :
   * - les lignes panier doivent être recalculées
   *
   * Si l’utilisateur se déconnecte :
   * - les lignes doivent revenir au prix public
   *
   * IMPORTANT
   * -------------------------------------------------------------
   * Ce comportement est central dans Marché Deluxe :
   * le panier ne doit jamais rester figé sur un prix obsolète
   * après login / logout.
   */
  useEffect(() => {
    if (!isHydrated) return;

    setPanier((prev) => {
      const safePrev = Array.isArray(prev) ? prev.map(normalizeCartItem) : [];
      if (!safePrev.length) return prev;

      let hasChanged = false;

      const next = safePrev.map((item) => {
        const repriced = applyPricingSnapshot(item, profilPricing, userProfil);

        const currentPrice = Number(item?.price || 0);
        const nextPrice = Number(repriced?.price || 0);

        const currentApplied = Number(item?.appliedPrice || 0);
        const nextApplied = Number(repriced?.appliedPrice || 0);

        const currentMode = String(item?.pricingMode || "");
        const nextMode = String(repriced?.pricingMode || "");

        if (
          currentPrice !== nextPrice ||
          currentApplied !== nextApplied ||
          currentMode !== nextMode
        ) {
          hasChanged = true;
          return normalizeCartItem(repriced);
        }

        return item;
      });

      return hasChanged ? next : prev;
    });
  }, [isHydrated, authStatus, profilPricing, userProfil]);

  /**
   * =============================================================
   * 3) CALCUL SOUS-TOTAL + PERSISTANCE
   * =============================================================
   *
   * IMPORTANT
   * -------------------------------------------------------------
   * Le sous-total technique du context doit suivre le prix appliqué
   * et non un price legacy figé.
   *
   * IMPORTANT
   * -------------------------------------------------------------
   * On persiste le panier tel qu’il est dans son état courant.
   * Ainsi, une reco ajoutée au panier devient une ligne normale
   * du panier persistant.
   */
  useEffect(() => {
    if (!isHydrated) return;

    const total = (Array.isArray(panier) ? panier : []).reduce((acc, item) => {
      const priceCents =
        Number(item?.appliedPrice ?? item?.displayPrice ?? item?.price) || 0;

      const qty = Number(item?.qty ?? 1) || 1;
      const grams = Number(item?.grams || 0) || 0;
      const gramsMultiplier = grams > 0 ? grams / 100 : 1;

      return acc + priceCents * gramsMultiplier * qty;
    }, 0);

    setSousTotal(Math.round(total));

    try {
      localStorage.setItem(LS_KEY, JSON.stringify(panier));
    } catch {
      /**
       * ---------------------------------------------------------
       * Silencieux volontaire
       * ---------------------------------------------------------
       * Un problème localStorage ne doit pas casser le panier.
       */
    }
  }, [panier, isHydrated]);

  /**
   * =============================================================
   * 4) ACTION — AJOUT AU PANIER
   * =============================================================
   *
   * OBJECTIF
   * -------------------------------------------------------------
   * Lorsqu’un produit entre dans le panier, on veut garder :
   * - le prix affiché maintenant
   * - le prix public
   * - le prix membre
   * - les champs legacy utiles
   * - une image exploitable immédiatement
   *
   * IMPORTANT CHANTIER RECOMMANDATIONS
   * -------------------------------------------------------------
   * Les produits ajoutés via le petit bouton + des recommandations
   * doivent entrer dans le panier avec des champs image suffisamment
   * riches pour être affichés dès le premier rendu.
   */
  const ajouterAuPanier = useCallback(
    (produit, qty = 1, grams = 0) => {
      const safeQty = Number(qty) || 1;
      const safeGrams = Number(grams) || 0;
      const cartKey = makeCartKey(produit?.id, safeGrams);

      /**
       * ---------------------------------------------------------
       * IMAGE SOURCE NORMALISÉE
       * ---------------------------------------------------------
       * On choisit ici une source image unique, puis on la recopie
       * dans les différentes variantes de champs utilisées par le projet.
       */
      const resolvedImage =
        produit?.imageUrl ||
        produit?.image_url ||
        produit?.image ||
        produit?.photo ||
        produit?.thumbnail ||
        "";

      /**
       * ---------------------------------------------------------
       * BUILD ITEM — VERSION RICHE
       * ---------------------------------------------------------
       * On sécurise explicitement les sources pricing critiques
       * et les champs image.
       */
      const baseDraft = normalizeCartItem({
        ...produit,
        qty: safeQty,
        grams: safeGrams,
        cartKey,
        unitLabel: safeGrams > 0 ? `${safeGrams}g` : produit?.unite,

        image: resolvedImage || produit?.image || "",
        imageUrl: resolvedImage || produit?.imageUrl || "",
        image_url: resolvedImage || produit?.image_url || "",
        photo: resolvedImage || produit?.photo || "",
        thumbnail: resolvedImage || produit?.thumbnail || "",

        /**
         * -------------------------------------------------------
         * PRIX APPLIQUÉ AU MOMENT DE L’AJOUT
         * -------------------------------------------------------
         */
        price:
          toCents(produit?.displayPrice) ??
          toCents(produit?.price) ??
          toCents(produit?.appliedPrice) ??
          0,

        displayPrice:
          toCents(produit?.displayPrice) ??
          toCents(produit?.price) ??
          toCents(produit?.appliedPrice) ??
          0,

        appliedPrice:
          toCents(produit?.displayPrice) ??
          toCents(produit?.appliedPrice) ??
          toCents(produit?.price) ??
          0,

        /**
         * -------------------------------------------------------
         * SOURCES PRIX
         * -------------------------------------------------------
         */
        basePrice:
          toCents(produit?.basePrice) ??
          toCents(produit?.publicPrice) ??
          toCents(produit?.old_price) ??
          toCents(produit?.displayBasePrice) ??
          toCents(produit?.price) ??
          0,

        publicPrice:
          toCents(produit?.publicPrice) ??
          toCents(produit?.basePrice) ??
          toCents(produit?.old_price) ??
          toCents(produit?.displayBasePrice) ??
          toCents(produit?.price) ??
          0,

        memberPrice:
          toCents(produit?.memberPrice) ??
          toCents(produit?.pricePro) ??
          toCents(produit?.prix_connecte) ??
          toCents(produit?.prix_membre) ??
          toCents(produit?._audit?.targetPrice),

        pricePro:
          toCents(produit?.pricePro) ??
          toCents(produit?.memberPrice) ??
          toCents(produit?._audit?.targetPrice),

        prix_connecte:
          toCents(produit?.prix_connecte) ??
          toCents(produit?.memberPrice) ??
          toCents(produit?.pricePro) ??
          toCents(produit?._audit?.targetPrice),

        prix_membre:
          toCents(produit?.prix_membre) ??
          toCents(produit?.memberPrice) ??
          toCents(produit?.pricePro) ??
          toCents(produit?._audit?.targetPrice),

        old_price:
          toCents(produit?.old_price) ??
          toCents(produit?.basePrice) ??
          toCents(produit?.displayBasePrice),

        pricingMode:
          produit?.pricingMode || produit?._audit?.pricingMode || "PUBLIC",
      });

      /**
       * ---------------------------------------------------------
       * SNAPSHOT PRICING IMMÉDIAT
       * ---------------------------------------------------------
       * On aligne l’item sur le contexte utilisateur courant.
       */
      const enrichedItem = normalizeCartItem(
        applyPricingSnapshot(baseDraft, profilPricing, userProfil)
      );

      setPanier((prev) => {
        const normalizedPrev = Array.isArray(prev)
          ? prev.map(normalizeCartItem)
          : [];

        const index = normalizedPrev.findIndex(
          (p) => (p.cartKey || makeCartKey(p.id, p.grams)) === cartKey
        );

        /**
         * -------------------------------------------------------
         * CAS 1 — LIGNE DÉJÀ EXISTANTE
         * -------------------------------------------------------
         * On fusionne puis on incrémente la quantité.
         * On garde aussi l’image si elle arrive via le nouvel item.
         */
        if (index > -1) {
          const updated = [...normalizedPrev];
          const existing = updated[index];

          const merged = normalizeCartItem(
            applyPricingSnapshot(
              {
                ...existing,
                ...enrichedItem,
                image:
                  enrichedItem?.image ||
                  enrichedItem?.imageUrl ||
                  existing?.image ||
                  existing?.imageUrl ||
                  "",
                imageUrl:
                  enrichedItem?.imageUrl ||
                  enrichedItem?.image ||
                  existing?.imageUrl ||
                  existing?.image ||
                  "",
                image_url:
                  enrichedItem?.image_url ||
                  enrichedItem?.imageUrl ||
                  enrichedItem?.image ||
                  existing?.image_url ||
                  existing?.imageUrl ||
                  existing?.image ||
                  "",
                photo:
                  enrichedItem?.photo ||
                  enrichedItem?.imageUrl ||
                  enrichedItem?.image ||
                  existing?.photo ||
                  existing?.imageUrl ||
                  existing?.image ||
                  "",
                thumbnail:
                  enrichedItem?.thumbnail ||
                  enrichedItem?.imageUrl ||
                  enrichedItem?.image ||
                  existing?.thumbnail ||
                  existing?.imageUrl ||
                  existing?.image ||
                  "",
                qty: (Number(existing?.qty) || 1) + safeQty,
                grams: safeGrams,
                cartKey,
                unitLabel:
                  safeGrams > 0 ? `${safeGrams}g` : existing?.unitLabel,
              },
              profilPricing,
              userProfil
            )
          );

          updated[index] = merged;
          return updated;
        }

        /**
         * -------------------------------------------------------
         * CAS 2 — NOUVELLE LIGNE
         * -------------------------------------------------------
         */
        return [...normalizedPrev, enrichedItem];
      });
    },
    [profilPricing, userProfil]
  );

  /**
   * =============================================================
   * 5) ACTION — RETIRER DU PANIER
   * =============================================================
   *
   * RÈGLE
   * -------------------------------------------------------------
   * Si grams n’est pas fourni :
   * - on retire toutes les lignes du même id
   *
   * Si grams est fourni :
   * - on retire la ligne exacte
   */
  const retirerDuPanier = useCallback((produitId, grams = null) => {
    setPanier((prev) => {
      const normalizedPrev = Array.isArray(prev)
        ? prev.map(normalizeCartItem)
        : [];

      if (grams === null || grams === undefined) {
        return normalizedPrev.filter((p) => String(p.id) !== String(produitId));
      }

      const key = makeCartKey(produitId, grams);
      return normalizedPrev.filter(
        (p) => (p.cartKey || makeCartKey(p.id, p.grams)) !== key
      );
    });
  }, []);

  /**
   * =============================================================
   * 6) ACTION — VIDER LE PANIER
   * =============================================================
   *
   * RÈGLE
   * -------------------------------------------------------------
   * Le panier est totalement vidé.
   * Utilisé notamment dans le flow logout confirmé.
   */
  const viderPanier = useCallback(() => setPanier([]), []);

  /**
   * =============================================================
   * 7) ACTION — UPDATE QUANTITÉ
   * =============================================================
   *
   * RÈGLE
   * -------------------------------------------------------------
   * Cette méthode reçoit une quantité absolue.
   * Elle ne reçoit pas un delta.
   */
  const updateQuantite = useCallback((produitId, qty, grams = null) => {
    const safeQty = Math.max(0, Number(qty) || 0);

    setPanier((prev) => {
      const normalizedPrev = Array.isArray(prev)
        ? prev.map(normalizeCartItem)
        : [];

      if (grams !== null && grams !== undefined) {
        const key = makeCartKey(produitId, grams);

        return normalizedPrev
          .map((p) =>
            (p.cartKey || makeCartKey(p.id, p.grams)) === key
              ? { ...p, qty: safeQty }
              : p
          )
          .filter((p) => (Number(p.qty) || 0) > 0);
      }

      return normalizedPrev
        .map((p) =>
          String(p.id) === String(produitId) ? { ...p, qty: safeQty } : p
        )
        .filter((p) => (Number(p.qty) || 0) > 0);
    });
  }, []);

  /**
   * =============================================================
   * 8) VALEUR EXPOSÉE PAR LE CONTEXT
   * =============================================================
   *
   * IMPORTANT
   * -------------------------------------------------------------
   * On expose uniquement l’API stable du panier.
   * Pas de logique recommandation ici : elle reste côté PagePanier.
   */
  return (
    <PanierContext.Provider
      value={{
        panier,
        sousTotal,
        ajouterAuPanier,
        retirerDuPanier,
        viderPanier,
        updateQuantite,
      }}
    >
      {children}
    </PanierContext.Provider>
  );
};
